// GamesListPanel — Phase 12.8 panel inside GamesHub.
// Was AdminSettings.tsx (the games-config landing page).
//
// Phase 5 UI overhaul (2026-05-15): subtitle text now uses the
// arena/neon token scale per the unified design system. The
// gradient-tile grid (GameSettingsTile) keeps its bold gradients
// — those are the games' signature aesthetic.

import { Link } from 'react-router-dom';
import { GAMES } from '@baaligames/shared-ui/games/registry';
import ApplyAllPanel from '@baaligames/shared-ui/admin/components/ApplyAllPanel';
import GameSettingsTile from '../../components/GameSettingsTile';

// GM-4: Football & Tennis ride the shared cricket engine and aren't in the
// shared-ui registry (that also feeds the agent end), so we surface them as
// synthetic tiles here. Only the fields GameSettingsTile reads are needed.
const API_SPORT_TILES = [
  { id: 'football', displayName: 'Football', tag: 'API sport', adminIcon: 'sports_soccer', theme: { gradient: 'from-emerald-600 to-green-900', emoji: '⚽' } },
  { id: 'tennis', displayName: 'Tennis', tag: 'API sport', adminIcon: 'sports_tennis', theme: { gradient: 'from-lime-500 to-teal-800', emoji: '🎾' } },
];

export default function GamesListPanel() {
  return (
    <div className="space-y-4">
      {/* Cross-game "Apply All" banner. Renders only when there are
          pending unpublished changes in at least one game; null
          otherwise. Future-proof: iterates the backend registry,
          so any new game registered with its own
          /:game/pending endpoint automatically shows up here. */}
      <ApplyAllPanel />

      {/* GM-4: MASTER SETTINGS — one place to turn any game on/off. Hide
          removes the lobby tile entirely; Lock keeps it visible but blocks
          play. Applies to every game, cricket/football/tennis included. */}
      <Link
        to="/settings/game-dev-security"
        className="flex items-center gap-3 rounded-xl px-4 py-3 border border-indigo-300 dark:border-indigo-700/70 bg-gradient-to-r from-indigo-50 to-white dark:from-indigo-950/40 dark:to-transparent hover:brightness-105 active:scale-[0.997] transition"
      >
        <span className="material-symbols-rounded text-indigo-600 dark:text-indigo-300 text-2xl shrink-0">shield_lock</span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-bold text-indigo-900 dark:text-indigo-200">Master control — Show / Hide &amp; Lock every game</div>
          <div className="text-[11.5px] text-indigo-700/80 dark:text-indigo-300/70 truncate">Per-game on/off in one grid: <b>Hide</b> (remove tile) or <b>Lock</b> (visible but unplayable).</div>
        </div>
        <span className="material-symbols-rounded text-indigo-400 dark:text-indigo-500">chevron_right</span>
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 max-w-3xl">
          Pick a game to control every aspect of how it plays — bet limits, bot behaviour, table stakes, recovery rules. Platform fees &amp; the turnover return live in Settings → Rebate &amp; platform fees.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/reports/target-profit-simulator"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/10">
            <span className="material-symbols-rounded text-base">tune</span>
            Simulator
          </Link>
          <Link to="/reports/target-profit"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-500 text-white">
            <span className="material-symbols-rounded text-base">insights</span>
            Target-Profit report
          </Link>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl">
        {GAMES.map((g) => (
          <GameSettingsTile key={g.id} game={g} />
        ))}
        {API_SPORT_TILES.map((g) => (
          <GameSettingsTile key={g.id} game={g as any} />
        ))}
      </div>
    </div>
  );
}
